<template>
  <form @submit.prevent="handleSubmit">
    <div class="form-field">
      <FranchiseSelect
        v-model="formData.so_service_priority_franchise_id"
        :label="t('Franquia')"
        data-testid="so-service-priorities-franchise-select"
        required
      />
    </div>
    <div class="flex-container">
      <div class="flex-item-half">
        <div class="form-field">
          <SelectList
            elem-id="soServicePriorityServiceId"
            data-testid="so-service-priorities-service-select"
            v-model="formData.so_service_priority_service_id"
            :itens-select="serviceOptions"
            :label="t('Serviço')"
            :placeholder="t('Selecione o serviço')"
            ng-required="true"
            :validation="validateServiceId"
            :auto-select-first="false"
          />
          <small v-if="errors.so_service_priority_service_id" class="error-message">{{ errors.so_service_priority_service_id }}</small>
        </div>
      </div>

      <div class="flex-item-half">
        <div class="form-field">
          <SelectList
            elem-id="soServicePriorityPriorityId"
            data-testid="so-service-priorities-priority-select"
            v-model="formData.so_service_priority_priority_id"
            :itens-select="priorityOptions"
            :label="t('Prioridade')"
            :placeholder="t('Selecione a prioridade')"
            ng-required="true"
            :validation="validatePriorityId"
            :auto-select-first="false"
          />
          <small v-if="errors.so_service_priority_priority_id" class="error-message">{{ errors.so_service_priority_priority_id }}</small>
        </div>
      </div>
    </div>

    <div class="flex-container">
      <div class="flex-item-half">
        <div class="form-field">
          <input-currency
            elem-id="soServicePriorityPrice"
            data-testid="so-service-priorities-price-input"
            v-model="formData.so_service_priority_price"
            :label="t('Preço')"
            :placeholder="t('Digite o preço')"
            ng-required="true"
            :validation="validatePrice"
          />
          <small v-if="errors.so_service_priority_price" class="error-message">{{ errors.so_service_priority_price }}</small>
        </div>
      </div>

      <div class="flex-item-half">
        <div class="form-field">
          <input-number
            elem-id="soServicePriorityDeadlineDays"
            data-testid="so-service-priorities-deadline-days-input"
            v-model="formData.so_service_priority_deadline_days"
            :label="t('Prazo (dias)')"
            :placeholder="t('Digite o prazo em dias')"
            :min="0"
            :maxFractionDigits="0"
            ng-required="true"
            :validation="validateDeadlineDays"
            fluid
          />
          <small v-if="errors.so_service_priority_deadline_days" class="error-message">{{ errors.so_service_priority_deadline_days }}</small>
        </div>
      </div>
    </div>

    <div class="form-field">
      <input-number
        elem-id="soServicePriorityOrder"
        data-testid="so-service-priorities-order-input"
        v-model="formData.so_service_priority_order"
        :label="t('Ordem de exibição')"
        :placeholder="t('Digite a ordem de exibição')"
        :min="0"
        :maxFractionDigits="0"
        fluid
      />
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import InputNumber from '../../inputs/InputNumber.vue';
import SelectList from '../../inputs/SelectList.vue';
import { useSOServicePriorities } from '../../composables/useSOServicePriorities';
import { useSOCatalog } from '../../composables/useSOCatalog';
import { eventBus } from '../../eventBus';
import { emitValidationFailed } from '../../composables/useFormValidation';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface Props {
  entryId?: Id | null;
  isEditMode?: boolean;
}

interface SelectOption {
  ID: Id;
  Name: string;
}

interface KVItem {
  value: Id;
  label: string;
}

interface PriorityEntry {
  so_service_priority_franchise_id?: Id | null;
  so_service_priority_service_id?: Id | string;
  so_service_priority_priority_id?: Id | string;
  so_service_priority_price?: number | string;
  so_service_priority_deadline_days?: number | string;
  so_service_priority_order?: number | string;
}

interface AxiosErrorLike {
  response?: { data?: { error?: string } };
  message?: string;
  stack?: string;
}

const props = withDefaults(defineProps<Props>(), {
  entryId: null,
  isEditMode: false
});

const emit = defineEmits<{
  'submit-success': [];
  'submit-error': [error: unknown];
}>();

// Get i18n composable
const { t } = useI18n();

// Use composables
const { getById, save } = useSOServicePriorities();
const { fetchKV: fetchServicesKV } = useSOCatalog('services');
const { fetchKV: fetchPrioritiesKV } = useSOCatalog('priorities');

// Convert entryId to number for consistency
const entryIdNumber = computed<number | null>(() => {
  if (!props.entryId) return null;
  const num = typeof props.entryId === 'string' ? parseInt(props.entryId, 10) : props.entryId;
  log('SOServicePrioritiesForm', 'entryIdNumber', 'Converted ID', { original: props.entryId, converted: num });
  return num;
});

// Reactive data
const formData = reactive<{
  so_service_priority_franchise_id: Id | null;
  so_service_priority_service_id: Id | undefined;
  so_service_priority_priority_id: Id | undefined;
  so_service_priority_price: number | undefined;
  so_service_priority_deadline_days: number | undefined;
  so_service_priority_order: number | undefined;
}>({
  so_service_priority_franchise_id: null,
  so_service_priority_service_id: undefined,
  so_service_priority_priority_id: undefined,
  so_service_priority_price: undefined,
  so_service_priority_deadline_days: undefined,
  so_service_priority_order: undefined
});

const errors = reactive<{
  so_service_priority_service_id: string;
  so_service_priority_priority_id: string;
  so_service_priority_price: string;
  so_service_priority_deadline_days: string;
}>({
  so_service_priority_service_id: '',
  so_service_priority_priority_id: '',
  so_service_priority_price: '',
  so_service_priority_deadline_days: ''
});

const isLoading = ref<boolean>(false);
const serviceOptions = ref<SelectOption[]>([]);
const priorityOptions = ref<SelectOption[]>([]);

// Validation functions
const validateServiceId = (): boolean => {
  const v = formData.so_service_priority_service_id;
  return v != null && v !== '';
};

const validatePriorityId = (): boolean => {
  const v = formData.so_service_priority_priority_id;
  return v != null && v !== '';
};

const validatePrice = (): boolean => {
  return formData.so_service_priority_price != null && formData.so_service_priority_price >= 0;
};

const validateDeadlineDays = (): boolean => {
  return formData.so_service_priority_deadline_days != null && formData.so_service_priority_deadline_days >= 0;
};

// Load KV options
const loadOptions = async (): Promise<void> => {
  log('SOServicePrioritiesForm', 'loadOptions', 'Loading service and priority options');
  try {
    const [servicesKV, prioritiesKV] = await Promise.all([
      fetchServicesKV() as Promise<KVItem[]>,
      fetchPrioritiesKV() as Promise<KVItem[]>
    ]);

    // Convert KV array [{value, label}] to [{ID, Name}] for SelectList
    serviceOptions.value = servicesKV.map((item: KVItem) => ({
      ID: item.value,
      Name: item.label
    }));

    priorityOptions.value = prioritiesKV.map((item: KVItem) => ({
      ID: item.value,
      Name: item.label
    }));

    log('SOServicePrioritiesForm', 'loadOptions', 'Options loaded', {
      services: serviceOptions.value.length,
      priorities: priorityOptions.value.length
    });
  } catch (error: unknown) {
    logError('SOServicePrioritiesForm', 'loadOptions', 'Error loading options', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao carregar opcoes') + ': ' + message);
  }
};

const loadForEdit = async (): Promise<void> => {
  if (!entryIdNumber.value) {
    logWarn('SOServicePrioritiesForm', 'loadForEdit', 'No entryId provided');
    return;
  }

  log('SOServicePrioritiesForm', 'loadForEdit', 'Loading entry for edit', entryIdNumber.value);
  isLoading.value = true;

  try {
    const entry = await getById(entryIdNumber.value) as PriorityEntry | null;
    log('SOServicePrioritiesForm', 'loadForEdit', 'Loaded entry data', entry);

    if (!entry) {
      throw new Error('Dados da entrada nao encontrados');
    }

    // Populate form - convert price from centavos to reais for display
    formData.so_service_priority_franchise_id = entry.so_service_priority_franchise_id || null;
    formData.so_service_priority_service_id = entry.so_service_priority_service_id ? parseInt(String(entry.so_service_priority_service_id), 10) : undefined;
    formData.so_service_priority_priority_id = entry.so_service_priority_priority_id ? parseInt(String(entry.so_service_priority_priority_id), 10) : undefined;
    formData.so_service_priority_price = entry.so_service_priority_price != null ? parseFloat(String(entry.so_service_priority_price)) / 100 : undefined;
    formData.so_service_priority_deadline_days = entry.so_service_priority_deadline_days != null ? parseInt(String(entry.so_service_priority_deadline_days), 10) : undefined;
    formData.so_service_priority_order = entry.so_service_priority_order != null ? parseInt(String(entry.so_service_priority_order), 10) : undefined;

    // Clear errors
    clearErrors();
  } catch (error: unknown) {
    const err = error as AxiosErrorLike;
    logError('SOServicePrioritiesForm', 'loadForEdit', 'Error loading entry', {
      entryId: entryIdNumber.value,
      error: err.message,
      stack: err.stack,
      response: err.response
    });

    const errorMessage = err.response?.data?.error || err.message || 'Erro desconhecido';
    eventBus.emit('globalMessage.error', t('Erro ao carregar entrada da matriz') + ': ' + errorMessage);
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

const validateForm = (): boolean => {
  log('SOServicePrioritiesForm', 'validateForm', 'Validating form');
  clearErrors();
  let isValid = true;

  if (!formData.so_service_priority_service_id) {
    errors.so_service_priority_service_id = t('Campo obrigatório');
    isValid = false;
  }

  if (!formData.so_service_priority_priority_id) {
    errors.so_service_priority_priority_id = t('Campo obrigatório');
    isValid = false;
  }

  if (formData.so_service_priority_price == null || formData.so_service_priority_price < 0) {
    errors.so_service_priority_price = t('Campo obrigatório');
    isValid = false;
  }

  if (formData.so_service_priority_deadline_days == null || formData.so_service_priority_deadline_days < 0) {
    errors.so_service_priority_deadline_days = t('Campo obrigatório');
    isValid = false;
  }

  log('SOServicePrioritiesForm', 'validateForm', 'Validation result', isValid);
  if (!isValid) {
    emitValidationFailed('SOServicePrioritiesForm', errors);
    return false;
  }
  return isValid;
};

const clearErrors = (): void => {
  errors.so_service_priority_service_id = '';
  errors.so_service_priority_priority_id = '';
  errors.so_service_priority_price = '';
  errors.so_service_priority_deadline_days = '';
};

const resetForm = (): void => {
  log('SOServicePrioritiesForm', 'resetForm', 'Resetting form');
  formData.so_service_priority_franchise_id = null;
  formData.so_service_priority_service_id = undefined;
  formData.so_service_priority_priority_id = undefined;
  formData.so_service_priority_price = undefined;
  formData.so_service_priority_deadline_days = undefined;
  formData.so_service_priority_order = undefined;
  clearErrors();
};

// Build the optional display order slice of the save payload — only
// included when the user supplied a value, so empty fields don't overwrite
// existing values with 0.
const orderPayload = (): { so_service_priority_order?: number } => {
  const slice: { so_service_priority_order?: number } = {};
  if (formData.so_service_priority_order != null) {
    slice.so_service_priority_order = parseInt(String(formData.so_service_priority_order), 10);
  }
  return slice;
};

const handleSubmit = async (): Promise<void> => {
  log('SOServicePrioritiesForm', 'handleSubmit', 'Form submitted');

  if (!validateForm()) {
    logWarn('SOServicePrioritiesForm', 'handleSubmit', 'Validation failed');
    return;
  }

  isLoading.value = true;

  try {
    if (props.isEditMode && entryIdNumber.value) {
      // Update existing entry
      const payload = {
        so_service_priority_service_id: Number(formData.so_service_priority_service_id),
        so_service_priority_priority_id: Number(formData.so_service_priority_priority_id),
        so_service_priority_price: Math.round((formData.so_service_priority_price || 0) * 100),
        so_service_priority_deadline_days: parseInt(String(formData.so_service_priority_deadline_days || 0), 10),
        so_service_priority_id: entryIdNumber.value,
        ...orderPayload()
      };
      log('SOServicePrioritiesForm', 'handleSubmit', 'Updating entry', payload);
      await save(payload);
      eventBus.emit('globalMessage.success', t('Entrada da matriz atualizada com sucesso'));
    } else {
      // Create new entry
      const payload = {
        so_service_priority_service_id: Number(formData.so_service_priority_service_id),
        so_service_priority_priority_id: Number(formData.so_service_priority_priority_id),
        so_service_priority_price: Math.round((formData.so_service_priority_price || 0) * 100),
        so_service_priority_deadline_days: parseInt(String(formData.so_service_priority_deadline_days || 0), 10),
        so_service_priority_franchise_id: formData.so_service_priority_franchise_id !== null ? Number(formData.so_service_priority_franchise_id) : undefined,
        ...orderPayload()
      };
      log('SOServicePrioritiesForm', 'handleSubmit', 'Creating entry', payload);
      await save(payload);
      eventBus.emit('globalMessage.success', t('Entrada da matriz criada com sucesso'));
    }

    emit('submit-success');
    resetForm();
  } catch (error: unknown) {
    logError('SOServicePrioritiesForm', 'handleSubmit', 'Error submitting form', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error',
      props.isEditMode
        ? t('Erro ao atualizar entrada da matriz') + ': ' + message
        : t('Erro ao criar entrada da matriz') + ': ' + message
    );
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

// Watch for changes in entryId to reload data
watch(() => props.entryId, async (newId) => {
  log('SOServicePrioritiesForm', 'watch', 'entryId changed', { newId, isEditMode: props.isEditMode });
  if (newId && props.isEditMode) {
    await nextTick();
    await loadForEdit();
  }
});

// Lifecycle
onMounted(async () => {
  log('SOServicePrioritiesForm', 'mounted', 'Component mounted', {
    entryId: props.entryId,
    entryIdNumber: entryIdNumber.value,
    isEditMode: props.isEditMode
  });

  // Load dropdown options on mount
  await loadOptions();

  if (props.isEditMode && entryIdNumber.value) {
    await loadForEdit();
  }
});

// Expose methods for parent components
defineExpose({
  loadForEdit,
  handleSubmit,
  resetForm,
  validateForm
});
</script>

<style scoped>
.flex-container {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.flex-item-half {
  flex: 1;
  min-width: 0;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.error-message {
  color: var(--p-red-500);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}
</style>
